import { cloneDeep, isEmpty } from "lodash";
import React from "react";
import FadeIn from "../../../../src/vendored/FadeIn";
import { useHistory } from "react-router";
import { ClipLoader } from "react-spinners";
import { common_utils } from "../../../../common/common_utils";
import { Constants } from "../../../../common/constants";
import {
	ChannelOverrideSettingsDeleteRequest,
	DiscordAuthToken,
	GetGuildRolesAndChannelsResult,
	GuildChannelSettingsOverrides,
	GuildData,
	SeshTextChannelData,
} from "../../../../common/sesh_types";
import { DashboardUtils, FADE_IN_ANIMATION_DURATION } from "../dashboard_utils";

interface CategoryData {
	is_category: boolean;
	id: string;
	name: string;
	position: number;
	channels: SeshTextChannelData[];
}

function ChannelOverrideElement(props: {
	channel_data: SeshTextChannelData;
	guild_id: string;
	is_overridden: boolean;
	should_add_bottom_border: boolean;
	is_deleting: boolean;
	on_delete_channel_overrides: (channel_id: string) => void;
}) {
	const history = useHistory();

	let className = "channel-element";

	if (props.should_add_bottom_border) {
		className += "  channel-element-bottom-border";
	}

	if (props.is_overridden) {
		className += " channel-element-overridden";
	}

	const icon =
		props.channel_data.type === "Text" ? (
			<i className="fas fa-hashtag"></i>
		) : (
			<i className="fas fa-comments fa-sm"></i>
		);

	return (
		<div className={className}>
			<div
				className="columns is-vcentered is-mobile ml-2"
				onClick={() => {
					history.push(
						common_utils.getGuildChannelSettingsURL(
							props.guild_id,
							props.channel_data.id
						)
					);
				}}
			>
				<div className="column is-one-third-tablet is-two-thirds-mobile">
					<div className="flex items-center gap-2">
						<div>{icon}</div>
						<div className="w-full min-w-0 truncate">
							{props.channel_data.name}
						</div>
					</div>
				</div>

				{props.is_overridden && (
					<div className="column is-one-third is-hidden-mobile is-centered has-text-centered">
						<span className="italic-note has-text-centered">
							Overrides Active
						</span>
					</div>
				)}

				{props.is_overridden && (
					<div className="column is-one-third is-pulled-right has-text-right">
						{(props.is_deleting && (
							<ClipLoader
								size={"1rem"}
								loading={true}
								color={Constants.EMBED_COLOR}
							/>
						)) || (
							<span
								className="tooltip delete-channel-override-icon"
								onClick={(e) => {
									e.stopPropagation();
									props.on_delete_channel_overrides(
										props.channel_data.id
									);
								}}
							>
								<i className="fas fa-trash-alt"></i>
								<span className="tooltiptop">
									{"Delete overrides for this channel"}
								</span>
							</span>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

function CategoryElement(props: {
	category_data: CategoryData;
	guild_id: string;
}) {
	return (
		<div className="category-element">
			<p>{props.category_data.name.toUpperCase()}</p>
		</div>
	);
}

interface ChannelSettingsPickerProps {
	guild_data: GuildData;
	channel_settings_overrides: GuildChannelSettingsOverrides;
	roles_and_channels: GetGuildRolesAndChannelsResult;
	auth_token: DiscordAuthToken;
	on_save_func: (deleted_override_channel_id: string) => void;
}

interface ChannelSettingsPickerState {
	delete_being_processed_channel_ids: string[];
}
export class ChannelSettingsPickerForm extends React.Component<
	ChannelSettingsPickerProps,
	ChannelSettingsPickerState
> {
	constructor(props) {
		super(props);

		this.state = {
			delete_being_processed_channel_ids: [],
		};
	}

	renderChannelOverridesList() {
		type ChannelOrCategory = CategoryData | SeshTextChannelData;

		// category_id -> CategoryData
		const categories_map = new Map<string, CategoryData>();
		const channels_and_categories_flat_list: ChannelOrCategory[] = [];

		// populate channels_and_categories_flat_list and categories_map
		for (let channel of this.props.roles_and_channels.channels) {
			if (channel.type === "Thread" || !channel.has_write) {
				continue;
			}

			if (channel.category_id) {
				if (!categories_map.has(channel.category_id)) {
					const new_category = {
						is_category: true,
						id: channel.category_id,
						name: channel.category_name,
						position: channel.category_position,
						channels: [],
					};

					categories_map.set(channel.category_id, new_category);
				}

				let category_data = categories_map.get(channel.category_id);
				category_data.channels.push(channel);
			} else {
				channels_and_categories_flat_list.push(channel);
			}
		}

		// sort
		let categories_list = Array.from(categories_map.values());
		categories_list.sort((a, b) => {
			return a.position - b.position;
		});

		for (let category of categories_list) {
			channels_and_categories_flat_list.push(category);
			category.channels.sort((a, b) => {
				return a.position - b.position;
			});
			for (let channel of category.channels) {
				channels_and_categories_flat_list.push(channel);
			}
		}

		const channel_elements: JSX.Element[] = [];

		let channel_index_counter = 0;
		let last_category: CategoryData = null;

		for (let channel_or_category of channels_and_categories_flat_list) {
			if (channel_or_category.hasOwnProperty("is_category")) {
				channel_elements.push(
					<CategoryElement
						category_data={channel_or_category as CategoryData}
						guild_id={this.props.guild_data.id}
						key={channel_or_category.id}
					/>
				);
				channel_index_counter = 0;
				last_category = channel_or_category as CategoryData;
			} else {
				let channel = channel_or_category as SeshTextChannelData;

				let should_add_bottom_border = false;
				if (last_category && last_category.channels.includes(channel)) {
					const last_channel_in_category =
						last_category.channels[
							last_category.channels.length - 1
						];

					if (last_channel_in_category !== channel) {
						should_add_bottom_border = true;
					}
				}

				let is_overridden =
					this.props.channel_settings_overrides.overrides[
						channel.id
					] &&
					!isEmpty(
						this.props.channel_settings_overrides.overrides[
							channel.id
						]
					);

				channel_elements.push(
					<ChannelOverrideElement
						channel_data={
							channel_or_category as SeshTextChannelData
						}
						guild_id={this.props.guild_data.id}
						key={channel_or_category.id}
						is_overridden={is_overridden}
						should_add_bottom_border={should_add_bottom_border}
						is_deleting={this.state.delete_being_processed_channel_ids.includes(
							channel.id
						)}
						on_delete_channel_overrides={(channel_id) => {
							let new_deletes_being_processed = cloneDeep(
								this.state.delete_being_processed_channel_ids
							);
							new_deletes_being_processed.push(channel_id);

							this.setState({
								delete_being_processed_channel_ids:
									new_deletes_being_processed,
							});

							const request_body: ChannelOverrideSettingsDeleteRequest =
								{
									access_token:
										this.props.auth_token.access_token,
									token_type:
										this.props.auth_token.token_type,
									guild_id: this.props.guild_data.id,
									channel_id: channel_id,
								};

							fetch(`/api/delete_channel_override_settings`, {
								method: "POST",
								headers: {
									"Content-Type": "application/json",
								},
								body: JSON.stringify(request_body),
							}).then(async (response) => {
								let new_deletes_being_processed = cloneDeep(
									this.state
										.delete_being_processed_channel_ids
								).filter((val) => val !== channel_id);

								this.setState({
									delete_being_processed_channel_ids:
										new_deletes_being_processed,
								});

								if (response.status === 200) {
									this.props.on_save_func(channel_id);
								} else {
									let response_json = await response.json();

									DashboardUtils.ShowDefaultErrorAlert(
										response_json.err
									);
								}
							});
						}}
					/>
				);
				channel_index_counter++;
			}
		}

		return <div className="mt-4">{channel_elements}</div>;
	}

	render() {
		return (
			<FadeIn transitionDuration={FADE_IN_ANIMATION_DURATION}>
				<div
					className="columns is-mobile is-centered"
					style={{ transition: "opacity 0.5s", opacity: "1" }}
				>
					<div className="column is-11-mobile is-three-quarters-tablet is-three-quarters-desktop is-half-widescreen is-two-fifths-fullhd create-event-container">
						<div className="settings-link-chain-container">
							<DashboardUtils.RenderLinkChain
								link_chain_data={[
									DashboardUtils.GetGuildSelectionLinkChainData(),
									DashboardUtils.RenderGuildOptionSelectLinkChain(
										this.props.guild_data
									),
									DashboardUtils.RenderGuildSettingsLinkChain(
										this.props.guild_data.id
									),
									DashboardUtils.RenderGuildChannelPickerLinkChain(
										this.props.guild_data.id
									),
								]}
							/>
						</div>
						<h1 className="title is-3 has-text-centered mt-6">
							Channel Overrides
						</h1>
						<div className="note">
							<p className="">
								Channel overrides let you change settings on a
								per-channel basis.
							</p>
							<p className="mt-2">
								This is great for customizing the default
								timezone, sign up options, or nearly anything
								other setting per channel!
							</p>
							<p className="mt-2">
								Select a channel below to get started.
							</p>
						</div>
						{this.renderChannelOverridesList()}
					</div>
				</div>
			</FadeIn>
		);
	}
}
